import { sqliteService } from './SQLiteService';
import { networkManager } from './NetworkManager';
import { syncQueue } from './SyncQueue';

/**
 * OfflineManager - Initializes and manages all offline services
 * Call this once when your app starts
 */
export class OfflineManager {
  private static instance: OfflineManager;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): OfflineManager {
    if (!OfflineManager.instance) {
      OfflineManager.instance = new OfflineManager();
    }
    return OfflineManager.instance;
  }

  /**
   * Initialize all offline services
   * Call this in your App.tsx or main component
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('🔄 Offline services already initialized');
      return;
    }

    try {
      console.log('🚀 Initializing offline services...');

      // 1. Initialize SQLite database
      await sqliteService.initializeDatabase();

      // 2. Network manager is automatically initialized (singleton)
      console.log('🌐 Network manager ready');

      // 3. Start automatic sync if online
      if (networkManager.getConnectionStatus()) {
        console.log('🔄 Starting initial sync...');
        setTimeout(() => {
          syncQueue.processPendingItems();
        }, 2000); // Delay to allow app to fully load
      }

      // 4. Set up periodic cleanup
      this.setupPeriodicCleanup();

      this.isInitialized = true;
      console.log('✅ Offline services initialized successfully');

      // Log initial stats
      await this.logInitialStats();

    } catch (error) {
      console.error('❌ Failed to initialize offline services:', error);
      throw error;
    }
  }

  /**
   * Get system status and statistics
   */
  async getSystemStatus(): Promise<{
    isOnline: boolean;
    database: any;
    syncQueue: any;
    lastSync: string | null;
  }> {
    const dbStats = await sqliteService.getDatabaseStats();
    const queueStats = await syncQueue.getQueueStats();
    const lastSync = await sqliteService.getMetadata('last_full_sync');

    return {
      isOnline: networkManager.getConnectionStatus(),
      database: dbStats,
      syncQueue: queueStats,
      lastSync
    };
  }

  /**
   * Force sync all data
   */
  async forceSyncAll(): Promise<void> {
    console.log('🚀 Force syncing all offline data...');
    
    if (!networkManager.getConnectionStatus()) {
      throw new Error('Cannot sync while offline');
    }

    await syncQueue.forceSyncAll();
    await sqliteService.setMetadata('last_full_sync', new Date().toISOString());
    
    console.log('✅ Force sync completed');
  }

  /**
   * Clear all offline data (emergency reset)
   */
  async clearAllData(): Promise<void> {
    console.log('🗑️ Clearing all offline data...');
    
    // Clear SQLite database
    await sqliteService.clearAllData();
    
    // Clear any pending sync items
    await syncQueue.clearAllPending();
    
    console.log('✅ All offline data cleared');
  }

  /**
   * Export offline data for debugging
   */
  async exportData(): Promise<any> {
    const tables = [
      'vehicles', 'drivers', 'vehicle_assignments',
      'vehicle_receiving_forms', 'vehicle_deliveries', 
      'vehicle_damages', 'accounting_transactions'
    ];

    const exportData: any = {
      timestamp: new Date().toISOString(),
      tables: {}
    };

    for (const table of tables) {
      try {
        const data = await sqliteService.select(table);
        exportData.tables[table] = data;
      } catch (error: any) {
        console.warn(`Failed to export ${table}:`, error);
        exportData.tables[table] = { error: error?.message || 'Unknown error' };
      }
    }

    // Include sync queue
    exportData.sync_queue = await sqliteService.select('sync_queue');
    exportData.metadata = await sqliteService.select('offline_metadata');

    return exportData;
  }

  /**
   * Setup periodic cleanup tasks
   */
  private setupPeriodicCleanup(): void {
    // Clean up old completed sync items every hour
    setInterval(() => {
      syncQueue.clearOldCompletedItems(7); // Keep for 7 days
    }, 60 * 60 * 1000); // 1 hour

    // Force connectivity check every 5 minutes
    setInterval(() => {
      networkManager.forceConnectivityCheck();
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Log initial system statistics
   */
  private async logInitialStats(): Promise<void> {
    try {
      const stats = await this.getSystemStatus();
      console.log('📊 Offline System Status:', {
        online: stats.isOnline,
        totalRecords: Object.values(stats.database).reduce((sum: number, table: any) => sum + (table.total || 0), 0),
        pendingSync: Object.values(stats.database).reduce((sum: number, table: any) => sum + (table.needsSync || 0), 0),
        queuedItems: stats.syncQueue.pending,
        lastSync: stats.lastSync
      });
    } catch (error) {
      console.warn('Failed to log initial stats:', error);
    }
  }

  /**
   * Cleanup resources
   */
  async destroy(): Promise<void> {
    await sqliteService.closeDatabase();
    networkManager.destroy();
    this.isInitialized = false;
    console.log('🧹 Offline services cleaned up');
  }
}

// Export singleton instance
export const offlineManager = OfflineManager.getInstance();

/**
 * React Hook for offline system status
 */
import { useState, useEffect } from 'react';
import { useNetworkStatus } from './NetworkManager';

export function useOfflineStatus() {
  const { isOnline } = useNetworkStatus();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const systemStatus = await offlineManager.getSystemStatus();
        setStats(systemStatus);
      } catch (error) {
        console.error('Failed to load offline stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();

    // Refresh stats every 30 seconds
    const interval = setInterval(loadStats, 30000);

    return () => clearInterval(interval);
  }, []);

  return {
    isOnline,
    stats,
    loading,
    refresh: async () => {
      setLoading(true);
      const systemStatus = await offlineManager.getSystemStatus();
      setStats(systemStatus);
      setLoading(false);
    },
    forceSync: () => offlineManager.forceSyncAll(),
    clearData: () => offlineManager.clearAllData()
  };
}